import { useEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { MdWarningAmber } from "react-icons/md";
import { useNavigate, useOutletContext } from "react-router-dom";
import MeritoButton from "../../MeritoButtons/MeritoButtons";
import type {
  SyncPhase,
  TallyConnectionFlowResult,
} from "../../../Hooks/useTallyConnectionFlow";
import { TallyConstantKey, TallyConstants } from "../constants";
import TallyResponsiveFrame from "../TallyResponsiveFrame";
import sharedStyles from "../TallySyncShared.module.scss";
import styles from "./TallySyncInstall.module.scss";

const supplierIntelligenceBasePath = TallyConstants.get(
  TallyConstantKey.SupplierIntelligenceBasePath
);
const tallyIntegrationVideoUrls = [
  TallyConstants.get(TallyConstantKey.TallyIntegrationStep1VideoUrl),
  TallyConstants.get(TallyConstantKey.TallyIntegrationStep2VideoUrl),
  TallyConstants.get(TallyConstantKey.TallyIntegrationStep3VideoUrl),
  TallyConstants.get(TallyConstantKey.TallyIntegrationStep4VideoUrl),
];
const tallySupportMobileNumber = TallyConstants.get(
  TallyConstantKey.TallySupportMobileNumber
);
const lastVideoIndex = tallyIntegrationVideoUrls.length - 1;
const carouselGapPx = 8;
const carouselPeekOffsetPx = 32;
const carouselPeekDelayMs = 650;
const carouselTransitionMs = 350;
const carouselPeekHoldMs = 300;

interface TallySyncInstallProps {
  isDashboard?: boolean;
  phase?: SyncPhase;
}

export default function TallySyncInstall({
  isDashboard = false,
  phase: phaseProp,
}: TallySyncInstallProps) {
  const navigate = useNavigate();
  const outletContext = useOutletContext<TallyConnectionFlowResult | undefined>();
  const phase = isDashboard ? phaseProp ?? "idle" : outletContext?.phase ?? "idle";
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  const peekTimerRefs = useRef<number[]>([]);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [isPeeking, setIsPeeking] = useState(false);
  const [isPeekComplete, setIsPeekComplete] = useState(false);
  const [unavailableVideos, setUnavailableVideos] = useState<
    Record<number, boolean>
  >({});

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;

      if (index !== activeVideoIndex) {
        video.pause();
        video.currentTime = 0;
        return;
      }

      if (!isPeekComplete) {
        video.pause();
        video.currentTime = 0;
        return;
      }

      video.currentTime = 0;
      void video.play().catch(() => {
        // Muted autoplay can still be blocked by browser or OS preferences.
      });
    });
  }, [activeVideoIndex, isPeekComplete]);

  useEffect(() => {
    if (
      tallyIntegrationVideoUrls.length < 2 ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setIsPeekComplete(true);
      return;
    }

    const showPeekTimer = window.setTimeout(() => {
      setIsPeeking(true);
    }, carouselPeekDelayMs);
    const hidePeekTimer = window.setTimeout(
      () => {
        setIsPeeking(false);
      },
      carouselPeekDelayMs + carouselTransitionMs + carouselPeekHoldMs
    );
    const completePeekTimer = window.setTimeout(
      () => {
        setIsPeekComplete(true);
      },
      carouselPeekDelayMs +
        carouselTransitionMs +
        carouselPeekHoldMs +
        carouselTransitionMs
    );

    peekTimerRefs.current = [
      showPeekTimer,
      hidePeekTimer,
      completePeekTimer,
    ];

    return () => {
      peekTimerRefs.current.forEach((timer) => window.clearTimeout(timer));
      peekTimerRefs.current = [];
    };
  }, []);

  const cancelPeek = () => {
    peekTimerRefs.current.forEach((timer) => window.clearTimeout(timer));
    peekTimerRefs.current = [];
    setIsPeeking(false);
    setIsPeekComplete(true);
  };

  const selectVideo = (index: number) => {
    if (index < 0 || index > lastVideoIndex) return;

    cancelPeek();

    if (index === activeVideoIndex) {
      const activeVideo = videoRefs.current[index];
      if (!activeVideo || unavailableVideos[index]) return;

      activeVideo.currentTime = 0;
      void activeVideo.play().catch(() => {});
      return;
    }

    setActiveVideoIndex(index);
  };

  const markVideoUnavailable = (index: number) => {
    setUnavailableVideos((current) =>
      current[index] ? current : { ...current, [index]: true }
    );
  };

  const stageContent = (
    <div
      className={
        isDashboard ? styles.dashboardContent : sharedStyles.content
      }
    >
      {!isDashboard && (
        <div className={sharedStyles.header}>
          <p className={sharedStyles.stepLabel}>
            STEP 2 OF 2 • Connect Automatically
          </p>
          <h1 className={sharedStyles.heading}>
            Install the Connector in Tally
          </h1>
        </div>
      )}

      <div
        className={`${styles.installInstructions} ${
          isDashboard ? styles.dashboardInstructions : ""
        }`}
      >
        <div className={styles.carouselColumn}>
          <div className={styles.carouselViewport}>
            <div
              className={styles.carouselTrack}
              style={{
                transform: `translateX(calc(-${
                  activeVideoIndex * 100
                }% - ${
                  activeVideoIndex * carouselGapPx +
                  (isPeeking && activeVideoIndex === 0
                    ? carouselPeekOffsetPx
                    : 0)
                }px))`,
              }}
            >
              {tallyIntegrationVideoUrls.map((videoUrl, index) => (
                <div
                  className={styles.carouselSlide}
                  key={videoUrl}
                  aria-hidden={index !== activeVideoIndex}
                >
                  <video
                    ref={(video) => {
                      videoRefs.current[index] = video;
                    }}
                    src={videoUrl}
                    className={styles.installVideo}
                    muted
                    playsInline
                    loop
                    preload={index === activeVideoIndex ? "auto" : "metadata"}
                    disablePictureInPicture
                    aria-label={`Tally connector installation video ${
                      index + 1
                    } of ${tallyIntegrationVideoUrls.length}`}
                    onError={() => markVideoUnavailable(index)}
                  />
                  {unavailableVideos[index] && (
                    <div className={styles.videoUnavailable} role="status">
                      Video unavailable
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div
            className={styles.carouselControls}
            role="group"
            aria-label="Installation video controls"
          >
            <MeritoButton
              type="tertiary"
              size="sm"
              onlyIcon
              isRounded
              hover={false}
              className={styles.carouselArrow}
              disabled={activeVideoIndex === 0}
              onClick={() => {
                selectVideo(activeVideoIndex - 1);
              }}
            >
              <FaChevronLeft aria-hidden="true" />
              <span className={styles.visuallyHidden}>
                Previous installation video
              </span>
            </MeritoButton>

            <div className={styles.carouselPagination}>
              {tallyIntegrationVideoUrls.map((videoUrl, index) => (
                <MeritoButton
                  type="tertiary"
                  size="sm"
                  onlyIcon
                  isRounded
                  hover={false}
                  key={videoUrl}
                  className={`${styles.carouselDot} ${
                    index === activeVideoIndex
                      ? styles.carouselDotActive
                      : ""
                  }`}
                  extraStyles={{ padding: 0 }}
                  onClick={() => selectVideo(index)}
                >
                  <span className={styles.visuallyHidden}>
                    {index === activeVideoIndex ? "Current" : "Play"}{" "}
                    installation video {index + 1}
                  </span>
                </MeritoButton>
              ))}
            </div>

            <MeritoButton
              type="tertiary"
              size="sm"
              onlyIcon
              isRounded
              hover={false}
              className={styles.carouselArrow}
              disabled={activeVideoIndex === lastVideoIndex}
              onClick={() => {
                selectVideo(activeVideoIndex + 1);
              }}
            >
              <FaChevronRight aria-hidden="true" />
              <span className={styles.visuallyHidden}>
                Next installation video
              </span>
            </MeritoButton>
          </div>
        </div>

        <div className={styles.installStepsPanel}>
          {isDashboard && (
            <div className={styles.dashboardHeader}>
              <h2>Install the Connector in Tally</h2>
              <p>
                Add the file you downloaded to Tally, then click{" "}
                <strong>Sync with Merito</strong> when you&apos;re ready.
              </p>
            </div>
          )}

          <div className={styles.installStepsList}>
            <div className={styles.installStepRow}>
              <span className={sharedStyles.stepBadge}>1</span>
              <p className={styles.installStepText}>
                In Tally, go to Help <kbd>F1</kbd> → TDLs &amp; Addons.
              </p>
            </div>

            <div className={styles.installStepRow}>
              <span className={sharedStyles.stepBadge}>2</span>
              <div className={styles.installStepTextGroup}>
                <p className={styles.installStepText}>
                  Under Manage local TDLs, press <kbd>F4</kbd>, paste the file
                  path of the TDL and select Yes.
                </p>
                <p className={styles.installStepHint}>
                  For Tally Cloud, add the TDL file in the{" "}
                  {isDashboard ? <em>Data</em> : "Data"} folder.
                </p>
              </div>
            </div>

            <div className={styles.installStepRow}>
              <span className={sharedStyles.stepBadge}>3</span>
              <div className={styles.installStepTextGroup}>
                <p className={styles.installStepText}>
                  Back on Tally, click the new{" "}
                  <strong>Sync with Merito</strong> button.
                </p>
                <p className={styles.installStepHint}>
                  Merito only fetches data when you click this.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.supportRow}>
            <span>Stuck on a step?</span>
            <a
              href={`https://wa.me/${tallySupportMobileNumber}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Talk To Support
            </a>
          </div>

          {phase === "failed" && (
            <div className={styles.troubleshootingCard} role="alert">
              <div className={styles.troubleshootingHeader}>
                <MdWarningAmber
                  className={styles.troubleshootingIcon}
                  aria-hidden="true"
                />
                <p className={styles.troubleshootingTitle}>
                  Can&apos;t see &quot;Sync with Merito&quot; in Tally?
                </p>
              </div>
              <p className={styles.troubleshootingDescription}>
                The TDL may not be set up correctly. Revisit the steps above,
                then click Sync with Merito again.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (isDashboard) return stageContent;

  return (
    <TallyResponsiveFrame>
      <div className={sharedStyles.page}>
        <div className={sharedStyles.shell}>
          <MeritoButton
            type="secondary-1"
            size="md2"
            className={sharedStyles.backBtn}
            onClick={() =>
              navigate(
                `${supplierIntelligenceBasePath}/setup/upload/tally/sync`,
                { replace: true }
              )
            }
            icon={
              <span className={sharedStyles.backIcon} aria-hidden="true">
                <FaChevronLeft />
              </span>
            }
            btnText="Back"
          />

          <div className={sharedStyles.card}>{stageContent}</div>
        </div>
      </div>
    </TallyResponsiveFrame>
  );
}
